import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  Tabs,
  Tab,
  Snackbar,
  CircularProgress,
  Badge,
} from "@mui/material";
import {
  CalendarToday,
  Person,
  Email,
  Phone,
  Business,
  AttachMoney,
  Visibility,
  RefreshOutlined,
  Dashboard,
  DirectionsCar,
  ShoppingCart,
  PictureAsPdf,
  Assessment,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { useAuth } from "../../context/context";
import usePendingChangesCount from "../../hooks/usePendingChangesCount";
import { DataGrid } from "@mui/x-data-grid";
import { useQuotesManager } from "../../hooks/useQuotesManager";
import { PRIORITIES, QUOTE_STATUSES } from "../../constants/QuotesConstants";
import { QuoteStats } from "./components/QuoteStats";
import { QuoteFilters } from "./components/QuoteFilters";
import { DirectSalesList } from "./components/DirectSalesList";
import { PendingChangesList } from "./components/PendingChangesList";
import { ReportsList } from "./components/ReportsList";
import { useSearchParams } from "react-router-dom";
import { QuoteDetailModal } from "../Quotes/components/QuoteDetailModal";

// Componente principal del Backoffice
const Backoffice = () => {
  const { user, isAdmin, token } = useAuth();
  const { count: pendingCount } = usePendingChangesCount();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    quotes,
    loading,
    pagination,
    filters,
    updateFilters,
    changePage,
    updateQuoteStatusPriority,
    updateLoading,
  } = useQuotesManager({
    isBackoffice: true,
    itemsPerPage: 25,
  });

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tabValue, setTabValue] = useState(0);
  const [searchApplied, setSearchApplied] = useState(false);
  const [tabApplied, setTabApplied] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState({});

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const searchQuery = searchParams.get("search");

    if (tabParam && !tabApplied) {
      const tabIndex = parseInt(tabParam);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        setTabValue(tabIndex);
        setTabApplied(true);
        console.log("🔖 Backoffice: Abriendo tab", tabIndex);
      }
    }

    if (
      searchQuery &&
      searchQuery.trim() !== "" &&
      !searchApplied &&
      filters.search !== searchQuery &&
      tabValue === 0
    ) {
      console.log("🔍 Backoffice: Aplicando búsqueda desde URL:", searchQuery);
      setSearchApplied(true);
      updateFilters({ search: searchQuery });
    }
  }, [
    searchParams,
    updateFilters,
    searchApplied,
    filters.search,
    tabValue,
    tabApplied,
  ]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);

    const currentSearch = searchParams.get("search");
    const newParams = new URLSearchParams();

    if (currentSearch) {
      newParams.set("search", currentSearch);
    }

    if (newValue !== 0) {
      newParams.set("tab", newValue.toString());
    }

    setSearchParams(newParams);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("es-CL", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Debes iniciar sesión para acceder al backoffice.
        </Alert>
      </Container>
    );
  }

  if (!isAdmin() || user.areaId !== 1) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Solo administradores del área BIENES pueden acceder a esta sección.
        </Alert>
      </Container>
    );
  }

  const handleQuoteUpdate = async (quoteId, updates) => {
    try {
      await updateQuoteStatusPriority(quoteId, updates);
      setSnackbar({
        open: true,
        message: "Cotización actualizada exitosamente",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error al actualizar cotización",
        severity: "error",
      });
    }
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setSelectedQuote(null);
  };

  const handleModalStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatusPriority(quoteId, { status: newStatus });
      if (selectedQuote && selectedQuote.id === quoteId) {
        setSelectedQuote({ ...selectedQuote, status: newStatus });
      }
      setSnackbar({
        open: true,
        message: "Estado actualizado exitosamente",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error al actualizar estado",
        severity: "error",
      });
      throw error;
    }
  };

  const handleModalPriorityChange = async (quoteId, newPriority) => {
    try {
      await updateQuoteStatusPriority(quoteId, { priority: newPriority });
      if (selectedQuote && selectedQuote.id === quoteId) {
        setSelectedQuote({ ...selectedQuote, priority: newPriority });
      }
      setSnackbar({
        open: true,
        message: "Prioridad actualizada exitosamente",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error al actualizar prioridad",
        severity: "error",
      });
      throw error;
    }
  };

  const clearFilters = () => {
    updateFilters({
      status: "",
      priority: "",
      date_from: null,
      date_to: null,
      search: "",
    });

    setSearchParams({});
  };

  const handleDownloadPdf = async (quote) => {
    if (!token) {
      setSnackbar({
        open: true,
        message: "Debes iniciar sesión para descargar el PDF",
        severity: "error",
      });
      return;
    }

    try {
      setDownloadingPdf((prev) => ({ ...prev, [quote.id]: true }));

      const { productsService } =
        await import("../../services/productsService");

      await productsService.quotes.downloadAndSavePdf(
        quote.id,
        quote.quote_id,
        token,
      );

      setSnackbar({
        open: true,
        message: `PDF descargado exitosamente: ${quote.quote_id}`,
        severity: "success",
      });
    } catch (error) {
      console.error("❌ Error descargando PDF:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error al descargar el PDF",
        severity: "error",
      });
    } finally {
      setDownloadingPdf((prev) => ({ ...prev, [quote.id]: false }));
    }
  };

  const columns = [
    {
      field: "quote_id",
      headerName: "ID Cotización",
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.value || "N/A"}
          color="primary"
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "created_at",
      headerName: "Fecha",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.3,
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2">{formatDate(params.value)}</Typography>
        </Box>
      ),
    },
    {
      field: "client_name",
      headerName: "Cliente",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.3,
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {params.value || "N/A"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row?.client_email || "Sin email"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "product_data",
      headerName: "Producto",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const productData = params.value || {};
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.3,
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography variant="body2" fontWeight="medium" noWrap>
              {productData.name || params.row?.product_name || "N/A"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {productData.categoria || productData.category || "Sin categoría"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Estado",
      flex: 1.1,
      minWidth: 170,
      renderCell: (params) => {
        const status = params.value || "pending";

        // Colores de fondo según estado
        const getStatusBgColor = (status) => {
          switch (status) {
            case "pending":
              return { bg: "#fff3e0", border: "#ff9800", text: "#e65100" }; // Naranja
            case "in_progress":
            case "contacted":
              return { bg: "#e3f2fd", border: "#2196f3", text: "#1565c0" }; // Azul
            case "completed":
              return { bg: "#e8f5e9", border: "#4caf50", text: "#2e7d32" }; // Verde
            case "rejected":
              return { bg: "#ffebee", border: "#f44336", text: "#c62828" }; // Rojo
            case "cancelled":
              return { bg: "#fafafa", border: "#9e9e9e", text: "#616161" }; // Gris
            default:
              return { bg: "#f5f5f5", border: "#bdbdbd", text: "#757575" };
          }
        };

        const colors = getStatusBgColor(status);

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.3,
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <FormControl size="small" fullWidth>
              <Select
                value={status}
                onChange={(e) =>
                  handleQuoteUpdate(params.row.id, { status: e.target.value })
                }
                disabled={updateLoading[params.row?.id]}
                MenuProps={{ disableScrollLock: true }}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: colors.bg,
                  borderColor: colors.border,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                    borderWidth: 2,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "& .MuiSelect-select": {
                    color: colors.text,
                    fontWeight: 600,
                    py: 0.5,
                  },
                  "& .MuiSvgIcon-root": {
                    color: colors.text,
                  },
                }}
              >
                {Object.entries(QUOTE_STATUSES).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {config.icon}
                      {config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      },
    },
    {
      field: "priority",
      headerName: "Prioridad",
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) => {
        const priority = params.value || "normal";

        // Colores de fondo según prioridad
        const getPriorityBgColor = (priority) => {
          switch (priority) {
            case "urgent":
              return { bg: "#ffebee", border: "#d32f2f", text: "#b71c1c" }; // Rojo intenso
            case "high":
              return { bg: "#fff3e0", border: "#f57c00", text: "#e65100" }; // Naranja
            case "normal":
              return { bg: "#e3f2fd", border: "#1976d2", text: "#0d47a1" }; // Azul
            case "low":
              return { bg: "#f5f5f5", border: "#9e9e9e", text: "#616161" }; // Gris
            default:
              return { bg: "#f5f5f5", border: "#bdbdbd", text: "#757575" };
          }
        };

        const colors = getPriorityBgColor(priority);

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.3,
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <FormControl size="small" fullWidth>
              <Select
                value={priority}
                onChange={(e) =>
                  handleQuoteUpdate(params.row.id, { priority: e.target.value })
                }
                disabled={updateLoading[params.row?.id]}
                MenuProps={{ disableScrollLock: true }}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: colors.bg,
                  borderColor: colors.border,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                    borderWidth: 2,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "& .MuiSelect-select": {
                    color: colors.text,
                    fontWeight: 600,
                    py: 0.5,
                  },
                  "& .MuiSvgIcon-root": {
                    color: colors.text,
                  },
                }}
              >
                {Object.entries(PRIORITIES).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Chip
                      label={config.label}
                      color={config.color}
                      size="small"
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.8,
      minWidth: 120,
      maxWidth: 140,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Tooltip title="Descargar PDF">
            <IconButton
              size="small"
              onClick={() => handleDownloadPdf(params.row)}
              color="secondary"
              disabled={downloadingPdf[params.row?.id]}
            >
              {downloadingPdf[params.row?.id] ? (
                <CircularProgress size={18} />
              ) : (
                <PictureAsPdf />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={() => handleViewQuote(params.row)}
              color="primary"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<Dashboard />}
              label="Dashboard de Cotizaciones"
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab
              icon={<ShoppingCart />}
              label="Ventas Directas"
              id="tab-1"
              aria-controls="tabpanel-1"
            />
            <Tab
              icon={
                <Badge badgeContent={pendingCount} color="error" max={99}>
                  <RefreshOutlined />
                </Badge>
              }
              label="Cambios pendientes"
              id="tab-2"
              aria-controls="tabpanel-2"
            />
            <Tab
              icon={<Assessment />}
              label="Reportes"
              id="tab-3"
              aria-controls="tabpanel-3"
            />
          </Tabs>
        </Paper>

        {tabValue === 0 && (
          <Box>
            <QuoteFilters
              filters={filters}
              onFiltersChange={updateFilters}
              onClearFilters={clearFilters}
              loading={loading}
            />
            <Paper sx={{ height: 600, width: "100%" }}>
              <DataGrid
                rows={quotes}
                columns={columns}
                pageSize={pagination.itemsPerPage}
                rowCount={pagination.totalItems}
                page={pagination.currentPage - 1}
                onPageChange={(page) => changePage(page + 1)}
                paginationMode="server"
                loading={loading}
                disableSelectionOnClick
                localeText={{
                  noRowsLabel: "No hay cotizaciones disponibles",
                  footerRowSelected: (count) =>
                    `${count} fila(s) seleccionada(s)`,
                }}
                getRowClassName={(params) => {
                  if (params.row.priority === "urgent") return "row-urgent";
                  if (params.row.priority === "high") return "row-high";
                  return "";
                }}
                sx={{
                  "& .row-urgent": {
                    backgroundColor: "rgba(244, 67, 54, 0.05)",
                  },
                  "& .row-high": {
                    backgroundColor: "rgba(255, 152, 0, 0.05)",
                  },
                }}
              />
            </Paper>
          </Box>
        )}

        {tabValue === 1 && <DirectSalesList />}

        {tabValue === 2 && <PendingChangesList />}

        {tabValue === 3 && <ReportsList />}

        <QuoteDetailModal
          open={openDialog}
          onClose={handleCloseModal}
          quote={selectedQuote}
          onStatusChange={handleModalStatusChange}
          onPriorityChange={handleModalPriorityChange}
          isBackoffice={true}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default Backoffice;
